/** @type {{ [key: string]: number }} */
const definedMultipliers = {
  "1in:1in": 1,
  "1/32in:1ft": 384,
  "3/64in:1ft": 256,
  "1/16in:1ft": 192,
  "3/32in:1ft": 128,
  "1/8in:1ft": 96,
  "3/16in:1ft": 64,
  "1/4in:1ft": 48,
  "3/8in:1ft": 32,
  "1/2in:1ft": 24,
  "3/4in:1ft": 16,
  "1in:1ft": 12,
  "1.5in:1ft": 8,
  "1in:10ft": 120,
  "1in:20ft": 240,
  "1in:30ft": 360,
  "1in:40ft": 480,
  "1in:50ft": 600,
  "1in:60ft": 720,
  "1in:70ft": 840,
  "1in:80ft": 960,
  "1in:90ft": 1080,
  "1in:100ft": 1200,
  "1in:200ft": 2400,
  "1in:300ft": 3600,
  "1in:400ft": 4800,
  "1:1": 1,
  "1:10": 10,
  "1:25": 25,
  "1:50": 50,
  "1:100": 100,
  "1:200": 200,
  "1:500": 500,
  "1:1000": 1000,
};

const units = {
  in: 1,
  ft: 12,
  mm: 1 / 25.4,
};

// Parse a single side of the scale, e.g. "1/16in" or "1ft"
function parseSide(side) {
  const match = side.trim().match(/^([\d.]+)\s*([a-z]*)$/);
  if (!match) {
    throw new Error(`Invalid scale component: "${side}"`);
  }
  const value = parseFloat(match[1]);
  const unit = match[2] || null;
  if (isNaN(value)) {
    throw new Error(`Invalid number in scale: "${side}"`);
  }
  return { value, unit };
}

function multiplier(scale) {
  if (typeof scale !== "string") throw new Error("Scale must be a string");

  if (definedMultipliers[scale] != undefined) return definedMultipliers[scale];

  // Split on colon
  const parts = scale.trim().toLowerCase().split(":");
  if (parts.length !== 2) {
    throw new Error(`Invalid scale format: "${scale}"`);
  }

  const left = parseSide(parts[0]);
  const right = parseSide(parts[1]);

  // Units must both be present or both absent
  if ((left.unit === null) !== (right.unit === null)) {
    throw new Error(
      `Scale must have units on both sides or neither: "${scale}"`,
    );
  }

  // Pure ratio like "1:50" — just return right/left
  if (left.unit === null) {
    return right.value / left.value;
  }

  // Validate units are known
  if (!(left.unit in units)) {
    throw new Error(`Unknown unit: "${left.unit}" (supported: in, ft, mm)`);
  }
  if (!(right.unit in units)) {
    throw new Error(`Unknown unit: "${right.unit}" (supported: in, ft, mm)`);
  }

  // Convert both sides to inches, then divide
  const pageInches = left.value * units[left.unit];
  const realInches = right.value * units[right.unit];

  return realInches / pageInches;
}

const translations = {
  "1ft-0in": "1ft",
  "1ft0in": "1ft",
};

const scaleOptions = [
  { label: `1" = 1"`, value: "1in:1in" },
  { label: `1/32" = 1'0"`, value: "1/32in:1ft" },
  { label: `3/64" = 1'0"`, value: "3/64in:1ft" },
  { label: `1/16" = 1'0"`, value: "1/16in:1ft" },
  { label: `3/32" = 1'0"`, value: "3/32in:1ft" },
  { label: `1/8" = 1'0"`, value: "1/8in:1ft" },
  { label: `3/16" = 1'0"`, value: "3/16in:1ft" },
  { label: `1/4" = 1'0"`, value: "1/4in:1ft" },
  { label: `3/8" = 1'0"`, value: "3/8in:1ft" },
  { label: `1/2" = 1'0"`, value: "1/2in:1ft" },
  { label: `3/4" = 1'0"`, value: "3/4in:1ft" },
  { label: `1" = 1'0"`, value: "1in:1ft" },
  { label: `1 1/2" = 1'0"`, value: "1.5in:1ft" },
  { label: `1" = 10'`, value: "1in:10ft" },
  { label: `1" = 20'`, value: "1in:20ft" },
  { label: `1" = 30'`, value: "1in:30ft" },
  { label: `1" = 40'`, value: "1in:40ft" },
  { label: `1" = 50'`, value: "1in:50ft" },
  { label: `1" = 60'`, value: "1in:60ft" },
  { label: `1" = 70'`, value: "1in:70ft" },
  { label: `1" = 80'`, value: "1in:80ft" },
  { label: `1" = 90'`, value: "1in:90ft" },
  { label: `1" = 100'`, value: "1in:100ft" },
  { label: `1" = 200'`, value: "1in:200ft" },
  { label: `1" = 300'`, value: "1in:300ft" },
  { label: `1" = 400'`, value: "1in:400ft" },
  { label: "1:1", value: "1:1" },
  { label: "1:10", value: "1:10" },
  { label: "1:25", value: "1:25" },
  { label: "1:50", value: "1:50" },
  { label: "1:100", value: "1:100" },
  { label: "1:200", value: "1:200" },
  { label: "1:500", value: "1:500" },
  { label: "1:1000", value: "1:1000" },
];

function getNearestScale(scale) {
  let min;

  const m = 1 / scale;

  for (const [key, value] of Object.entries(definedMultipliers)) {
    const diff = Math.abs(value - m);
    if (min === undefined || diff < min.diff) {
      min = { key, value, diff };
    }
  }

  if (min.diff / min.value < 0.025) {
    return min.key;
  }

  const mf = m.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return `1:${mf}`;
}

function getScaleLabel(value) {
  const option = scaleOptions.find((option) => option.value === value);
  return option ? option.label : value;
}

export {
  scaleOptions,
  definedMultipliers,
  multiplier,
  translations,
  getNearestScale,
  getScaleLabel,
};
